// components/industry-templates/RealEstateEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, MapPin, TrendingUp, Home } from 'lucide-react';

export const RealEstateEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Property Showcase */}
      {industryData?.property_showcase && (
        <Card className="border-0 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Building2 className="w-8 h-8" style={{ color: theme.primary }} />
              Property Showcase
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.property_showcase.map((property: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20 hover:scale-105 transition-transform">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{property.name}</h3>
                      <p className="text-slate-300 text-sm flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {property.location}
                      </p>
                    </div>
                    <Badge className="bg-yellow-500 text-black">
                      {property.type}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-3 mt-4">
                    <div className="bg-white/5 rounded p-2 text-center">
                      <p className="text-xs text-slate-400">Price</p>
                      <p className="font-bold" style={{ color: theme.primary }}>
                        {property.price_range}
                      </p>
                    </div>
                    <div className="bg-white/5 rounded p-2 text-center">
                      <p className="text-xs text-slate-400">Units</p>
                      <p className="font-bold text-white">{property.units}</p>
                    </div>
                  </div>
                  {property.amenities && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {property.amenities.slice(0, 3).map((amenity: string, i: number) => (
                        <Badge key={i} variant="outline" className="border-white/20 text-white text-xs">
                          {amenity}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Developers */}
      {industryData?.developers && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Building2 className="w-6 h-6" style={{ color: theme.primary }} />
              Participating Developers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {industryData.developers.map((developer: string, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg text-center border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="text-2xl mb-2">🏗️</div>
                  <p className="text-white font-medium text-sm">{developer}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Investment Seminar */}
      {industryData?.investment_seminar && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(71, 85, 105, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-6 h-6" style={{ color: theme.primary }} />
              Real Estate Investment Seminar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {industryData.investment_seminar.topics?.map((topic: string, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <p className="text-white font-medium">{topic}</p>
                </div>
              ))}
              {industryData.investment_seminar.speakers && (
                <div className="bg-white/5 rounded-lg p-4 mt-4">
                  <p className="text-slate-400 text-sm">Expert Speakers:</p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {industryData.investment_seminar.speakers.map((speaker: string, idx: number) => (
                      <Badge key={idx} style={{ backgroundColor: theme.primary }}>
                        {speaker}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Site Visits */}
      {industryData?.site_visits && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Home className="w-6 h-6" style={{ color: theme.primary }} />
              Complimentary Site Visits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white mb-3">Book your complimentary site visit to featured properties</p>
              <div className="flex gap-2">
                <Badge style={{ backgroundColor: theme.primary }} className="text-sm py-2 px-4">
                  Free Transportation
                </Badge>
                <Badge variant="outline" className="border-white/20 text-white text-sm py-2 px-4">
                  Expert Guide
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
